import app from 'app';
import 'c/AspectRatiosController';
import _ from 'lodash-extended';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';
import { AspectRatiosController } from 'c/AspectRatiosController';
import { workPool } from '@wochitnpm/helpers';

('use strict');

app.namespace.Model.StoryModel = Backbone.Model.extend({
  idAttribute: 'id',

  defaults: function () {
    return {
      id: null,
      title: 'New Story In Progress',
      topicId: app.rootStore.getters['user/type'].WOCHIT_OPS ? null : 2,
      _defaultTopic: true,
    };
  },

  parse: function (raw, options) {
    options = $.extend(
      {
        initTimelineData: true, // if false - timelineData will not be initialized
      },
      options
    );

    raw.title = app.normalizeEx(raw.title);
    raw.description = app.normalizeEx(raw.description, {
      replaceNLbySpace: false,
    });
    raw.narratedText = app.normalizeEx(raw.narratedText, {
      replaceNLbySpace: false,
    });

    // fix for empty topicId on feed base story
    if (
      !_.isDefined(raw.topicId) &&
      !app.rootStore.getters['user/type'].WOCHIT_OPS &&
      _.isDefined(raw.mostRelevantTopicAncestor) &&
      _.isNumber(raw.mostRelevantTopicAncestor)
    ) {
      raw.topicId = raw.mostRelevantTopicAncestor;
    }

    if (!_.isDefined(raw.transitionSelection)) {
      raw.transitionSelection = {
        autoShuffle: false,
        selectedPreset: null,
      };
    }

    app.currentTopicId = raw.topicId ? raw.topicId : this.defaults().topicId;

    if (_.isEmpty(raw.audioTracks)) {
      raw.audioTracks = [];
    }

    // make `timelineData` copy of `visualTimeline`,
    // if source not exists - it will be initialized with default data...
    // ... unless !`options.initTimelineData`
    if (_.isDefined(raw.visualTimeline)) {
      raw.timelineData = raw.visualTimeline;
      delete raw.visualTimeline;
    } else {
      raw.timelineData = null;
    }

    if (!_.isDefined(raw.aspectRatio)) {
      if (
        options.isNewStory ||
        raw.storyStartPoint === app.storyStartPoint.FEED
      ) {
        raw.aspectRatio =
          app.remoteStash.get('lastAspectRatio') || app.DEFAULT.ASPECT_RATIO;
      } else {
        raw.aspectRatio = app.DEFAULT.ASPECT_RATIO;
      }
    }

    if (_.isUndefined(app.ASPECT_RATIOS[raw.aspectRatio])) {
      app.dialog.error({
        type: app.dialog.types.ERROR_1,
        title: `The story aspect ratio '${raw.aspectRatio}' no longer supported.`,
        showContactCsm: true,
      });
      raw.aspectRatio = app.DEFAULT.ASPECT_RATIO;
      app.remoteStash.set('lastAspectRatio', void 0);
    }
    raw._aspectRatio = app.ASPECT_RATIOS[raw.aspectRatio];

    app.currentAspectRatio = raw.aspectRatio;

    // initialize timeline required object structure if empty
    if (options.initTimelineData) {
      if (!raw.timelineData) {
        raw.timelineData = {};
      }

      if (_.isEmpty(raw.timelineData.visualTracks)) {
        raw.timelineData.visualTracks = [];
      }

      // parse timelineData visual tracks
      _.each(raw.timelineData.visualTracks, (visualTrack) => {
        app.namespace.Controller.AssetController.parse(visualTrack);

        _.each(PictureInPictureAdapter.track(visualTrack), (overlayAsset) => {
          app.namespace.Controller.AssetController.parse(overlayAsset);
        });
      });
    }

    if (_.isEmpty(raw.assets)) {
      raw._hasNoAssets = true;
    }

    if ('assets' in raw) {
      _.each(raw.selectedAssets, function (asset) {
        app.prependHttpAcrossObject(asset);
      });

      // set visual tracks
      if (raw.timelineData) {
        // _.each(raw.timelineData.metaOrderedMediaSegments, function (segment) {
        if (_.isEmpty(raw.timelineData.visualTracks)) {
          // segment.visualTracks = [];
          raw.timelineData.visualTracks = [];
        } else {
          _.each(raw.timelineData.visualTracks, function (track) {
            app.prependHttpAcrossObject(track);
          });
        }

        // if (!_.isEmpty(segment.content) && !_.isEmpty(segment.mediaAsset)) {
        //   // text based segment
        //   if (_.isEmpty(segment.tokenDatas)) {
        //     segment.tokenDatas = [];
        //   }
        // }
        // });
      }
    }

    if (!raw.programmerId) {
      app.doctorController.report(`Missing programmerId in story id:${raw.id}`);
    }

    if (raw.segments) {
      raw.segments = _.filter(raw.segments, (segment) => {
        return !_.isEmpty(segment.audioSegmentContent?.asset);
      });
    }

    return raw;
  },

  initialize: function (raw, options) {
    this.options = $.extend({}, options);

    this.videoPosterModel = new app.namespace.Model.AssetModel(
      raw.videoPosterAsset,
      {
        parse: true,
      }
    );
    this.videoPosterQ = new Backbone.Collection();
    this.videoPosterQ.on('add', (model) => {
      // only if one, execute the q
      if (this.videoPosterQ.length <= 1) {
        this._runVideoPosterQ(model);
      }
    });

    this.listenTo(this, 'remove', this.free);
    this.listenTo(this, 'change:_themeInfo', this._themeInfoChanged);
    this.listenTo(this, 'change:aspectRatio', this._aspectRatioChanged);

    if (this.options.editable) {
      // handle configuration anomaly
      this.set({
        // reset bad value (0)
        minSelectableVideoSegmentTime: this.getProgrammer().get(
          'minSelectableVideoSegmentTime'
        ),
      });

      this.modelState = new Backbone.Model({
        '?': 'StoryModelState',
        isDirty: !!this.options.isDirty,
        persistedStoryData: {},
        persistedPreviewVideoData: {},
        persistedTimeLineData: {},
        persistedTranscriptLastModified: Date.now(),
        autosaveStore: {},
        timeOfLastSave: Date.now(),
        timeOfLastAutosave: Date.now(),
      });

      if (!_.isEmpty(this.get('_previewVideoUrl'))) {
        this.persistPreviewVideoData();
      }

      this.listenTo(this, 'all', (eventName) => {
        if (/^change:[^_]/.test(eventName)) {
          this.hasBeenChanged();
        }
      });
    }

    this.aspectRatiosController = new AspectRatiosController({ model: this });
  },

  getLayoutsCustomOverlays: function () {
    app.controller.storyPromise
      .done(() => {
        const themeInfo = app.storyController.story.getThemeInfo();
        const presets = themeInfo?.presets || [];
        this.set(
          '_layoutPresets',
          presets
            .filter((preset) => preset.isSceneLayout && preset.cropRectangle)
            .map((preset) => preset.id)
        );
      })
      .fail(() => {
        this.set('_layoutPresets', []);
      });
  },

  cancelAutosave: function (bCancel = true) {
    // console.debug('StoryModel::cancelAutosave', bCancel);
    this.modelState.set('cancelAutosave', bCancel);
  },

  hasBeenChanged: _.debounce(
    function () {
      if (
        !this.options.editable ||
        !app.rootStore.state.story ||
        this.modelState.get('cancelAutosave')
      ) {
        return;
      }
      // console.time('StoryModel::hasBeenChanged');

      const currentStoryData = this.serialize();
      const isDirty = this._isStoryDataChanged(currentStoryData);
      this.modelState.set('isDirty', isDirty);

      this.invalidateFullPreview(currentStoryData);

      this.trigger('story:changed', this.modelState, isDirty);

      // console.timeEnd('StoryModel::hasBeenChanged');
    },
    app.TIME.SECOND,
    { leading: false, trailing: true }
  ),

  _isStoryDataChanged: function (currentStoryData) {
    const persistedStoryData = this.modelState.get('persistedStoryData');
    const changedViaPersisted = !_.isEqual(
      persistedStoryData,
      currentStoryData
    );

    if (app.rootStore.getters['user/box'].isDev()) {
      const diffViaPersisted = _.diffObject(
        persistedStoryData,
        currentStoryData
      );
      console.debug(
        'StoryModel::_isStoryDataChanged',
        changedViaPersisted,
        Object.keys(diffViaPersisted)
      );
      // if (_.isFunction(console.diff)) {
      //   console.diff(persistedStoryData, currentStoryData);
      // }
    }
    return changedViaPersisted;
  },

  invalidateFullPreview: function (currentStoryData) {
    currentStoryData = currentStoryData || this.serialize();
    const previewVideoUrl = this.get('_previewVideoUrl');
    if (previewVideoUrl && this._isPreviewDataChanged(currentStoryData)) {
      app.api.fullPreview.remove(previewVideoUrl);
      this.set('_previewVideoUrl', null);
    }
  },

  /**
   * Check set of properties defining visual content of rendered preview (in video moderation)
   * @return {boolean}
   */
  _isPreviewDataChanged: function (currentStoryData) {
    const persistedPreviewVideoData = this.modelState.get(
      'persistedPreviewVideoData'
    );
    if (_.isEmpty(persistedPreviewVideoData)) {
      return false;
    }

    const persistedTranscriptLastModified = this.modelState.get(
      'persistedTranscriptLastModified'
    );
    if (
      app.rootStore.state.story.transcriptLastModified !==
      persistedTranscriptLastModified
    ) {
      return true;
    }

    const obj = this.preparePreviewVideoData(currentStoryData);

    return _.some(this.VIDEO_FULL_PREVIEW_FIELDS, (field) => {
      const isChanged = !_.isEqual(
        obj[field],
        persistedPreviewVideoData[field]
      );
      isChanged &&
        console.log(`StoryModel::_isPreviewDataChanged at '${field}' field`);
      return isChanged;
    });
  },

  VIDEO_FULL_PREVIEW_FIELDS: [
    'visualTimeline',
    'chosenBGMusic',
    'aspectRatio',
    'videoPosterAsset',
    'title',
    'enableVOSubtitles',
    'enableVOWaveform',
    'topicId',
    'attributedBy',
    'selectedThemeID',
    'transitionSelection',
    'audioTracks',
    'customOverlays',
  ],

  /**
   * Story model was saved, - try to clean current state if nothing was changed during the process
   * @param data {Object} that was sent to the server for save
   * @param original {Boolean} if original - don't mark as dirty
   */
  persistStory: function ({ data, original = false }) {
    const savedHashCode = app.getHashCode(data);
    const currentHashCode = original
      ? savedHashCode
      : app.getHashCode(this.serialize());

    // if story was changed during the async save operation - mark as dirty and save last story data
    this.modelState.set({
      timeOfLastSave: Date.now(),
      isDirty: savedHashCode !== currentHashCode,
      persistedStoryData: data,
      autosaveStore: data,
      cancelAutosave: false,
    });
  },

  /**
   * store data that was used for preview video rendering
   * @param currentStoryData
   */
  persistPreviewVideoData: function (currentStoryData) {
    if (!currentStoryData) {
      currentStoryData = this.serialize();
    }

    const obj = this.preparePreviewVideoData(currentStoryData);

    this.modelState.set('persistedPreviewVideoData', obj);
    this.modelState.set(
      'persistedTranscriptLastModified',
      app.rootStore.state.story.transcriptLastModified
    );
  },

  /**
   * removed all unnecessary data from story data for preview purposes
   * @param storyData
   */
  preparePreviewVideoData(storyData) {
    const obj = _.pick(storyData, this.VIDEO_FULL_PREVIEW_FIELDS);

    _.eachIn(obj, `visualTimeline.visualTracks`, (asset) => {
      asset.metadata = null;
      asset.thumbnail = null;
      asset.previewResultData = null;
      asset.realTimePreviewChecksum = null;
      asset.realTimePreviewResultData = null;
    });

    return obj;
  },

  checkAgdTransitions: function () {
    const transitionPresets = this.getThemeInfo().transitionPresets;
    const shouldAutoShuffleTransitions = !!this.get('transitionSelection')
      .autoShuffle;
    const transitionSelection = this.get('transitionSelection');
    let selectedPreset = transitionSelection.selectedPreset;

    if (!_.find(transitionPresets, (tp) => tp.name === selectedPreset)) {
      // current transition preset isn't available in the theme, let's fallback to the default one
      selectedPreset = this.getThemeInfo().defaultTransitionPreset;
      this.set(
        'transitionSelection',
        Object.assign({}, transitionSelection, { selectedPreset })
      );
    }

    if (!selectedPreset) {
      // probably an older story / theme without transitions, it's OK! just don't do anything
      return;
    }

    const transitionsPresetFind = transitionPresets.find(
      (transitionsGroup) => transitionsGroup.name === selectedPreset
    );

    if (!transitionsPresetFind) {
      // shouldn't happen, just to make sure everything's alright, and story's preset is available.
      app.doctorController.report('no transition group found ');
      return;
    }

    const transitionsPresetsFlatten = transitionPresets.reduce(
      (result, { name, transitions }) =>
        result.concat(transitions.map((t) => ({ name, ...t }))),
      []
    );

    const timelineData = this.getTimelineData();
    _.each(timelineData.visualTracks, (visualTrack) => {
      if (
        visualTrack.agd &&
        visualTrack.agd.transition &&
        visualTrack.agd.transition.wiperType === 'WIPER'
      ) {
        const wiperName = visualTrack.agd.transition.wiperName;
        const isWiperAvailable = _.find(
          transitionsPresetsFlatten,
          (t) => t.wochletName === wiperName
        );
        const pool = transitionsPresetFind.transitions;
        if (!isWiperAvailable) {
          // old transition isn't available in new aspect ratio, need to replace / remove
          if (visualTrack.agd.selectedByUser || !shouldAutoShuffleTransitions) {
            visualTrack.agd.transition.wiperType = null;
            visualTrack.agd.transition.wiperName = null;
            visualTrack.agd.transition.presetName = null;
            visualTrack.agd.transition.selectedByUser = false;
          } else if (shouldAutoShuffleTransitions && pool.length) {
            visualTrack.agd.transition.wiperType = 'WIPER';
            visualTrack.agd.transition.presetName = selectedPreset;
            visualTrack.agd.transition.wiperName =
              pool[Math.floor(Math.random() * pool.length)].wochletName;
          }
        }
      }
    });
  },

  getTransitionPresetByName(transitionPresetName) {
    return this.getThemeInfo().transitionPresets.find(
      (tp) => tp.name === transitionPresetName
    );
  },

  free: function () {
    console.debug('StoryModel::free');

    this.off();
    this.stopListening();
    this.aspectRatiosController = null;

    this.videoPosterQ.off();
    this.videoPosterQ.reset();
  },

  getProgrammer: function () {
    return app.programmers.get(this.get('programmerId'));
  },

  /**
   * Video Production On Demand (VPOD)
   * @return {boolean}
   */
  isNew: function () {
    const storyId = this.get('id');
    return !_.isDefined(storyId) || _.isEmpty(storyId);
  },

  /**
   * for easy access to timeline data
   * @return {*}
   */
  getTimelineData: function () {
    if (!_.isEmpty(this.get('timelineData'))) {
      return this.get('timelineData');
    }

    return null;
  },

  recalculateTime: function (narratedText, options) {
    return app.api.textTime.post(
      this.get('programmerId'),
      narratedText,
      options
    );
  },

  matchesSearchCriteria: function (rgPattern, searchIn) {
    const searchProperties = _.pick(this.attributes, searchIn);

    const match = _.some(searchProperties, function (prop) {
      if (_.isArray(prop)) {
        prop = prop.join(' ');
      }
      return rgPattern.test(prop);
    });
    return !!match;
  },

  /**
   * Strip auto-generated properties from given object
   * It doesn't operates on this but on argument, so can be used as static.
   * @static
   * @param obj IN/OUT {StoryModel.attributes Object}
   * @return same as argument (for convenience)
   */
  strip: function (obj) {
    _.each(obj, function (value, key) {
      if (/^[_|$]|^template$/.test(key)) {
        obj[key] = null;
        delete obj[key];
      }
    });

    return obj;
  },

  /**
   * called while serialization
   */
  serialize: function () {
    // console.time('StoryModel::serialize');
    let obj = _.cloneDeepAndClean(this.attributes);
    const story = app.rootStore.state.story;

    obj = this.strip(obj);
    obj.assets = null;

    obj.chosenBGMusic = story.storyBackgroundMusic;
    obj.enableVOSubtitles = story.enableVOSubtitles;
    obj.enableVOWaveform = story.enableVOWaveform;

    obj.videoPosterAsset = this.serializeVideoPosterModel();
    obj.selectedAssets = app.rootStore.getters.serializedAssets;
    obj.segments = app.rootStore.getters.serializedVoiceOverSegments;
    obj.audioTracks = story.audioTracks;
    obj.selectedThemeID = app.rootStore.state.story.themeInfo.themeId;

    // timeline specific serialization
    obj.timelineData.totalDisplayTime = story.timelineDuration;
    if (app.rootStore.getters.showStyleEditor) {
      obj.timelineData.themeOverrides = _.cloneDeep(
        app.rootStore.getters.themeOverrides
      );
    }

    if (!_.isEmpty(obj.timelineData.visualTracks)) {
      obj.timelineData.visualTracks =
        app.namespace.Controller.AssetController.serialize(
          obj.timelineData.visualTracks
        );
    }

    obj.visualTimeline = obj.timelineData;
    delete obj.timelineData;

    // console.timeEnd('StoryModel::serialize');
    return JSON.parse(JSON.stringify(obj));
  },

  _themeInfoChanged: function () {
    this.checkAgdTransitions();
  },

  getWipersCount: function () {
    let rv = 0;
    const timelineData = this.getTimelineData();

    _.each(timelineData.visualTracks, (visualTrack) => {
      if (
        _.isDefined(visualTrack) &&
        _.isDefined(visualTrack.agd) &&
        _.isDefined(visualTrack.agd.wiperType)
      ) {
        rv++;
      }
    });

    return rv;
  },

  getThemeInfo: function () {
    const themeInfo = this.get('_themeInfo');
    const ratio = this.get('aspectRatio');

    return _.isArray(themeInfo)
      ? _.head(_.filter(themeInfo, { aspectRatio: ratio }))
      : themeInfo;
  },

  _adjustAspectRatioOnVideoPoster: function () {
    const videoPosterModel = this.videoPosterModel;
    const ratio = this.get('_aspectRatio').ratio;
    const posterWidth = videoPosterModel.getMetadata('width');
    const posterHeight = videoPosterModel.getMetadata('height');
    let posterAdj;

    if (videoPosterModel.get('type')) {
      posterAdj = {
        x: videoPosterModel.getAGD('cropX'),
        y: videoPosterModel.getAGD('cropY'),
        x1: videoPosterModel.getAGD('cropX1'),
        y1: videoPosterModel.getAGD('cropY1'),
      };
      posterAdj.width = (posterAdj.x1 - posterAdj.x) * posterWidth;
      posterAdj.height = (posterAdj.y1 - posterAdj.y) * posterHeight;
      posterAdj.cropRatio = posterAdj.width / posterAdj.height;
      posterAdj.centerX = posterAdj.x + (posterAdj.x1 - posterAdj.x) / 2;
      posterAdj.centerY = posterAdj.y + (posterAdj.y1 - posterAdj.y) / 2;

      if (ratio >= 1 && posterAdj.cropRatio >= 1) {
        posterAdj.newHeight = posterAdj.height;
        posterAdj.newWidth = posterAdj.newHeight * ratio;
      } else if (ratio >= 1 && posterAdj.cropRatio < 1) {
        // noinspection JSSuspiciousNameCombination
        posterAdj.newWidth = posterAdj.height;
        posterAdj.newHeight = posterAdj.newWidth / ratio;
      } else {
        // noinspection JSSuspiciousNameCombination
        posterAdj.newHeight = posterAdj.width;
        posterAdj.newWidth = posterAdj.newHeight * ratio;
      }

      if (posterAdj.newWidth > posterWidth) {
        posterAdj.newWidth = posterWidth;
        posterAdj.newHeight = posterAdj.newWidth / ratio;
      }

      if (posterAdj.newHeight > posterHeight) {
        posterAdj.newHeight = posterHeight;
        posterAdj.newWidth = posterAdj.newHeight * ratio;
      }

      posterAdj.newWidthPercent = posterAdj.newWidth / posterWidth;
      posterAdj.newHeightPercent = posterAdj.newHeight / posterHeight;

      posterAdj.newX = posterAdj.centerX - posterAdj.newWidthPercent / 2;
      posterAdj.newY = posterAdj.centerY - posterAdj.newHeightPercent / 2;

      if (posterAdj.newX < 0) {
        posterAdj.newX = 0;
      }

      if (posterAdj.newY < 0) {
        posterAdj.newY = 0;
      }

      posterAdj.newX1 = posterAdj.newX + posterAdj.newWidthPercent;
      posterAdj.newY1 = posterAdj.newY + posterAdj.newHeightPercent;

      if (posterAdj.newX1 > 1) {
        posterAdj.newX1 = 1;
        posterAdj.newX = posterAdj.newX1 - posterAdj.newWidthPercent;
      }

      if (posterAdj.newY1 > 1) {
        posterAdj.newY1 = 1;
        posterAdj.newY = posterAdj.newY1 - posterAdj.newHeightPercent;
      }

      videoPosterModel.setAGD({
        cropX: posterAdj.newX,
        cropY: posterAdj.newY,
        cropX1: posterAdj.newX1,
        cropY1: posterAdj.newY1,
      });
    }
  },

  refreshTimelineRTPs: function () {
    const timelineData = this.getTimelineData();
    _.each(timelineData.visualTracks, (visualTrack) => {
      if (visualTrack.isSOTPlaceholder) {
        return;
      }
      app.resetVisualTrackRTP(visualTrack);
    });
    app.timelineGateway.refreshRtpLookup({ resetUndoRedo: true });
  },

  _aspectRatioChanged: function (model, newRatio) {
    const oldAspectRatio = this.get('_aspectRatio');
    this.set('_aspectRatio', _.extend({}, app.ASPECT_RATIOS[newRatio]));

    this._adjustAspectRatioOnVideoPoster();
    this._adjustThemeStoryCustomOverlays();

    app.currentAspectRatio = newRatio;

    if (this.options.editable) {
      _.delay(
        function () {
          this._rePreviewSelectedAssets();
        }.bind(this),
        app.TIME.MENTAL.λ
      );
    }

    // check if agd transitions should change according to new aspect ratio
    this.checkAgdTransitions();

    const timelineData = this.getTimelineData();

    // adjust visual tracks on timeline
    this.aspectRatiosController.setSurface();
    _.each(timelineData.visualTracks, (visualTrack) => {
      if (visualTrack.isSOTPlaceholder) {
        return;
      }

      app.resetVisualTrackRTP(visualTrack);

      if (
        visualTrack.previewResultData &&
        !app.assetType.isFRAGMENT(visualTrack)
      ) {
        visualTrack.previewResultData = null;
      }

      this.aspectRatiosController.adjustVisualTrack({
        visualTrack,
        oldWidth: oldAspectRatio.width,
        oldHeight: oldAspectRatio.height,
      });
    });

    app.rootStore.dispatch('CHANGE_THEME_INFO', {
      themeInfo: this.getThemeInfo(),
    });
    app.timelineGateway.afterAspectRatioChanged();
  },

  _adjustThemeStoryCustomOverlays: function () {
    const themeInfo = this.getThemeInfo();
    const themeStoryPresets = themeInfo?.storyPresets || [];
    const storyCustomOverlays = this.get('customOverlays');

    if (!_.isEmpty(storyCustomOverlays)) {
      _.each(storyCustomOverlays, (co) => {
        const preset = themeStoryPresets.find((preset) => preset.id === co.id);
        co.disabled = !preset;
        co.noRealtimePreview = !!preset?.noRealtimePreview;
      });
    }

    if (!_.isEmpty(themeStoryPresets)) {
      _.each(themeStoryPresets, (preset) => {
        const presetExists = storyCustomOverlays.find(
          (co) => co.id === preset.id
        );
        if (!presetExists) {
          storyCustomOverlays.push({
            id: preset.id,
            displayText: preset.displayText,
            fields: preset.fields,
            noRealtimePreview: preset.noRealtimePreview,
            disabled: false,
          });
        }
      });
    }

    this.set('customOverlays', storyCustomOverlays);
  },

  _rePreviewSelectedAssets: function () {
    _.each(app.rootStore.getters.selectedAssets, (asset) => {
      workPool.job.createAssetPreview.forChangedAspectRatio({
        asset: asset,
        programmer: app.rootStore.getters.storyProgrammer,
      });
    });
  },

  _runVideoPosterQ(model) {
    this.setVideoPosterAuto(model)
      .then(() => {
        this.videoPosterQ.reset();
      })
      .fail(() => {
        if (this.videoPosterQ.length) {
          this.videoPosterQ.shift();
          this._runVideoPosterQ(this.videoPosterQ.at(0));
        }
      });
  },

  serializeVideoPosterModel: function () {
    if (this.isVideoPosterEmpty()) {
      return null;
    } else {
      return this.videoPosterModel.pick([
        'id',
        'type',
        'agd',
        'thumbnail',
        'previewResultData',
        'metadata',
        'startTime',
        'endTime',
        'bucketDirectory',
        'dateSelected',
      ]);
    }
  },

  clearVideoPoster() {
    this.videoPosterModel.clear({ silent: true });
  },

  isVideoPosterEmpty: function () {
    return (
      !this.videoPosterModel || !this.videoPosterModel.getMetadata('assetUUID')
    );
  },

  /** called from addSelectedAsset plugin */
  adjustVideoPoster_onAssetSelected: function (asset) {
    if (this.isVideoPosterEmpty() && this.isModelPotentiallyThumbnail(asset)) {
      this.videoPosterQ.push(new app.namespace.Model.AssetModel(asset));
    }
  },

  adjustVideoPoster_onAssetUnselected: function (asset) {
    if (app.storyController.isVideoPoster(asset)) {
      this.clearVideoPoster();
    }
    if (this.isVideoPosterEmpty()) {
      this.setFirstAvailablePoster();
    }
  },

  setFirstAvailablePoster: function () {
    const candidates = this.getPotentialThumbnails();
    const candidate = candidates[candidates.length - 1];
    if (candidate) {
      this.setVideoPosterAuto(candidate);
    }
  },

  getPotentialThumbnails: function () {
    return _.filter(app.rootStore.getters.selectedAssets, (asset) => {
      return this.isModelPotentiallyThumbnail(asset);
    });
  },

  isModelPotentiallyThumbnail: function (asset) {
    asset = asset.attributes || asset;
    // infographic assets excluded
    return (
      asset.metadata.uploadStatus !== app.uploadStatus.FAILED &&
      (app.assetType.isIMAGE(asset) ||
        (app.assetType.isFRAGMENT(asset) &&
          !app.assetType.isSOCIALFRAGMENT(asset)))
    );
  },

  setVideoPosterAuto: function (model, options) {
    if (!(model instanceof app.namespace.Model.AssetModel)) {
      model = new app.namespace.Model.AssetModel(model, {
        parse: false,
      });
    }

    if (app.storyController.isVideoPoster(model)) {
      // nothing to do
      return new $.Deferred().resolve();
    }

    this.set('videoPosterSnapshotTime', null);
    this.clearVideoPoster();

    let width = null;
    let height = null;
    let x1;
    let y1;
    let x2;
    let y2;
    let startPoint = null;
    const FIXED_ASPECT_RATIO = 4 / 3;
    const imageDimensionsPromise = new $.Deferred();

    if (model.isFRAGMENT()) {
      const posterImage = new Image();
      posterImage.setAttribute(
        'src',
        app.namespace.Controller.AssetController.getVideoPosterUrl(model)
      );
      posterImage.onload = function () {
        width = posterImage.width;
        height = posterImage.height;
        imageDimensionsPromise.resolve();
      };
    } else {
      width = model.getMetadata('width');
      height = model.getMetadata('height');
      imageDimensionsPromise.resolve();
    }

    return imageDimensionsPromise.then(() => {
      if (
        width / height < FIXED_ASPECT_RATIO &&
        this.get('_aspectRatio').ratio >= 1
      ) {
        // 16:9 part of the image that is X=20% below the top of the image
        startPoint = parseInt(height - height * 0.8);
        x1 = 0;
        y1 = startPoint / height;
        x2 = 1;
        y2 =
          (startPoint +
            parseInt(
              (width * this.get('_aspectRatio').height) /
                this.get('_aspectRatio').width
            )) /
          height;
      } else {
        // a Stretch to 16:9
        const selectionHeight = parseInt(
          (width * this.get('_aspectRatio').height) /
            this.get('_aspectRatio').width
        );

        if (selectionHeight > height) {
          const selectionWidth = parseInt(
            (height * this.get('_aspectRatio').width) /
              this.get('_aspectRatio').height
          );
          startPoint = parseInt((width - selectionWidth) / 2);
          x1 = startPoint / width;
          y1 = 0;
          x2 = (startPoint + selectionWidth) / width;
          y2 = 1;
        } else {
          startPoint = parseInt((height - selectionHeight) / 2);
          x1 = 0;
          y1 = startPoint / height;
          x2 = 1;
          y2 = (startPoint + selectionHeight) / height;
        }
      }

      const poster = $.extend({}, model.attributes, {
        agd: {
          effectType: app.imageEffectType.CROP,
          cropX: x1,
          cropY: y1,
          cropX1: x2,
          cropY1: y2,
        },
      });
      this.videoPosterModel.set(poster, options);
    });
  },
});
